import type { OpeningHourRow } from '../../utils/foodMasterImages';
import { defaultOpeningHours, WEEK_DAYS } from '../../utils/foodMasterImages';
import { composeE164, localPhoneDigits, splitPhoneByDialCodes } from '../../utils/phoneDialCode';
import { FALLBACK_COUNTRY_DIAL_CODES } from '../../services/countryDialCodeService';

const DIAL_CODE_OPTIONS = FALLBACK_COUNTRY_DIAL_CODES.map((o) => o.dialCode);

function splitRestaurantPhone(raw?: string, storedCode?: string) {
  const value = String(raw || '').trim();
  if (!value) {
    return { local: '', countryCode: storedCode || '+91' };
  }
  if (value.startsWith('+')) {
    const split = splitPhoneByDialCodes(value, DIAL_CODE_OPTIONS);
    return {
      local: split.localNumber || localPhoneDigits(value),
      countryCode: split.dialCode || storedCode || '+91',
    };
  }
  return {
    local: localPhoneDigits(value),
    countryCode: storedCode || '+91',
  };
}

export type RestaurantVerificationStatus = 'pending' | 'approved' | 'rejected' | 'blocked';
export type RestaurantRevenueModel = 'commission' | 'subscription';

export interface RestaurantFormState {
  name: string;
  slug: string;
  description: string;
  ownerName: string;
  phone: string;
  phoneCountryCode: string;
  supportNumber: string;
  supportPhoneCountryCode: string;
  email: string;
  merchantId: string;
  logo: string;
  coverImage: string;
  addressLine: string;
  city: string;
  state: string;
  pincode: string;
  latitude: string;
  longitude: string;
  zoneCode: string;
  restaurantTypeCode: string;
  cuisineTypeCodes: string[];
  branchCode: string;
  gstNumber: string;
  fssaiNumber: string;
  tradeLicenseNumber: string;
  isPureVeg: boolean;
  deliveryEnabled: boolean;
  takeawayEnabled: boolean;
  dineInEnabled: boolean;
  minOrderAmount: number;
  openingHours: OpeningHourRow[];
  weeklyHoliday: string;
  verificationStatus: RestaurantVerificationStatus;
  revenueModel: RestaurantRevenueModel;
  vendorCommissionType: 'percentage' | 'fixed';
  vendorCommissionValue: number;
  partnerSubscriptionPlanId: string;
  isOpen: boolean;
  isActive: boolean;
  sendWelcomeEmail: boolean;
}

export const RESTAURANT_MASTER_TABS = [
  { id: 'basic', label: 'Basic Details' },
  { id: 'business', label: 'Business' },
  { id: 'settings', label: 'Settings' },
  { id: 'timing', label: 'Timing' },
  { id: 'approval', label: 'Approval & Revenue' },
  { id: 'documents', label: 'Documents' },
] as const;

export const VERIFICATION_STATUS_OPTIONS: { value: RestaurantVerificationStatus; label: string; color: 'warning' | 'success' | 'error' | 'default' }[] = [
  { value: 'pending', label: 'Pending', color: 'warning' },
  { value: 'approved', label: 'Approved', color: 'success' },
  { value: 'rejected', label: 'Rejected', color: 'error' },
  { value: 'blocked', label: 'Blocked', color: 'default' },
];

export const emptyRestaurantForm = (): RestaurantFormState => ({
  name: '',
  slug: '',
  description: '',
  ownerName: '',
  phone: '',
  phoneCountryCode: '+91',
  supportNumber: '',
  supportPhoneCountryCode: '+91',
  email: '',
  merchantId: '',
  logo: '',
  coverImage: '',
  addressLine: '',
  city: '',
  state: '',
  pincode: '',
  latitude: '',
  longitude: '',
  zoneCode: '',
  restaurantTypeCode: '',
  cuisineTypeCodes: [],
  branchCode: '',
  gstNumber: '',
  fssaiNumber: '',
  tradeLicenseNumber: '',
  isPureVeg: false,
  deliveryEnabled: true,
  takeawayEnabled: false,
  dineInEnabled: false,
  minOrderAmount: 99,
  openingHours: defaultOpeningHours(),
  weeklyHoliday: '',
  verificationStatus: 'pending',
  revenueModel: 'commission',
  vendorCommissionType: 'percentage',
  vendorCommissionValue: 15,
  partnerSubscriptionPlanId: '',
  isOpen: true,
  isActive: true,
  sendWelcomeEmail: true,
});

function merchantPhone(row: Record<string, unknown>): string {
  const merchant = row.merchantId;
  if (merchant && typeof merchant === 'object' && 'phone' in merchant) {
    return String((merchant as { phone?: string }).phone || '');
  }
  return '';
}

export function resolveRestaurantPhone(row: Record<string, unknown>): string {
  const direct = String(row.phone ?? '').trim();
  if (direct) return direct;
  const fromMerchant = merchantPhone(row).trim();
  if (fromMerchant) return fromMerchant;
  const meta = (row.metadata || {}) as Record<string, unknown>;
  return String(meta.phone || meta.contactPhone || '').trim() || '—';
}

export function resolveRestaurantSupport(row: Record<string, unknown>): string {
  const direct = String(row.supportNumber ?? '').trim();
  if (direct) return direct;
  const meta = (row.metadata || {}) as Record<string, unknown>;
  return String(meta.supportNumber || '').trim() || '—';
}

export function restaurantRowToForm(row: Record<string, unknown>): RestaurantFormState {
  const meta = (row.metadata || {}) as Record<string, unknown>;
  const addr = (row.address || {}) as Record<string, unknown>;
  const coords = (row.location as { coordinates?: number[] })?.coordinates || [];
  const modes = (row.serviceModes || {}) as Record<string, boolean>;
  const hours = (row.openingHours as OpeningHourRow[]) || [];
  const zone = row.zoneId as { code?: string } | string | undefined;
  const zoneCode = typeof zone === 'object' && zone?.code
    ? zone.code
    : String(row.zoneCode || meta.zoneCode || '');

  const phoneRaw = String(row.phone || merchantPhone(row) || '');
  const phoneSplit = splitRestaurantPhone(phoneRaw, String(meta.phoneCountryCode || ''));
  const supportSplit = splitRestaurantPhone(
    String(row.supportNumber || meta.supportNumber || ''),
    String(meta.supportPhoneCountryCode || ''),
  );

  return {
    name: String(row.name || ''),
    slug: String(row.slug || ''),
    description: String(row.description || ''),
    ownerName: String(row.ownerName || meta.ownerName || ''),
    phone: phoneSplit.local,
    phoneCountryCode: phoneSplit.countryCode,
    supportNumber: supportSplit.local,
    supportPhoneCountryCode: supportSplit.countryCode,
    email: String(row.email || ''),
    merchantId: String((row.merchantId as { _id?: string })?._id || row.merchantId || ''),
    logo: String(row.logo || ''),
    coverImage: String(row.coverImage || ''),
    addressLine: String(addr.line1 || addr.address || ''),
    city: String(addr.city || ''),
    state: String(addr.state || ''),
    pincode: String(addr.pincode || ''),
    latitude: coords[1] != null ? String(coords[1]) : '',
    longitude: coords[0] != null ? String(coords[0]) : '',
    zoneCode,
    restaurantTypeCode: String(row.restaurantType || meta.restaurantTypeCode || ''),
    cuisineTypeCodes: (row.cuisineTypes as string[]) || [],
    branchCode: String(meta.branchCode || ''),
    gstNumber: String(row.gstNumber || meta.gstNumber || ''),
    fssaiNumber: String(row.fssaiNumber || meta.fssaiNumber || ''),
    tradeLicenseNumber: String(row.tradeLicenseNumber || meta.tradeLicenseNumber || ''),
    isPureVeg: row.isPureVeg === true || meta.isPureVeg === true,
    deliveryEnabled: modes.delivery !== false,
    takeawayEnabled: modes.takeaway === true,
    dineInEnabled: modes.dineIn === true,
    minOrderAmount: Number(row.minOrderAmount || 99),
    openingHours: hours.length ? hours : defaultOpeningHours(),
    weeklyHoliday: String(row.weeklyHoliday || ''),
    verificationStatus: (() => {
      const raw = String(row.verificationStatus || 'pending');
      return (raw === 'verified' ? 'approved' : raw) as RestaurantVerificationStatus;
    })(),
    revenueModel: (String(row.revenueModel || 'commission') as RestaurantRevenueModel),
    vendorCommissionType: (row.vendorCommissionType as 'percentage' | 'fixed') || 'percentage',
    vendorCommissionValue: Number(row.vendorCommissionValue ?? 15),
    partnerSubscriptionPlanId: String(
      (row.partnerSubscriptionPlanId as { _id?: string })?._id || row.partnerSubscriptionPlanId || '',
    ),
    isOpen: row.isOpen !== false,
    isActive: row.isActive !== false,
    sendWelcomeEmail: false,
  };
}

export function restaurantFormToPayload(form: RestaurantFormState) {
  return {
    name: form.name,
    slug: normalizeRestaurantSlug(form.slug || form.name),
    description: form.description || undefined,
    ownerName: form.ownerName || undefined,
    sendWelcomeEmail: form.sendWelcomeEmail,
    cuisineTypes: form.cuisineTypeCodes,
    restaurantType: form.restaurantTypeCode,
    restaurantTypeCode: form.restaurantTypeCode,
    minOrderAmount: Number(form.minOrderAmount),
    phone: composeE164(form.phoneCountryCode, form.phone) || undefined,
    supportNumber: composeE164(form.supportPhoneCountryCode, form.supportNumber) || undefined,
    email: form.email || undefined,
    merchantId: form.merchantId || undefined,
    logo: form.logo || undefined,
    coverImage: form.coverImage || undefined,
    openingHours: form.openingHours,
    weeklyHoliday: form.weeklyHoliday || undefined,
    isOpen: form.isOpen,
    isActive: form.isActive,
    isPureVeg: form.isPureVeg,
    deliveryEnabled: form.deliveryEnabled,
    takeawayEnabled: form.takeawayEnabled,
    dineInEnabled: form.dineInEnabled,
    gstNumber: form.gstNumber || undefined,
    fssaiNumber: form.fssaiNumber || undefined,
    tradeLicenseNumber: form.tradeLicenseNumber || undefined,
    verificationStatus: form.verificationStatus,
    revenueModel: form.revenueModel,
    vendorCommissionType: form.vendorCommissionType,
    vendorCommissionValue: Number(form.vendorCommissionValue),
    partnerSubscriptionPlanId: form.partnerSubscriptionPlanId || undefined,
    zoneCode: form.zoneCode || undefined,
    addressLine: form.addressLine || undefined,
    city: form.city || undefined,
    state: form.state || undefined,
    pincode: form.pincode || undefined,
    latitude: form.latitude ? Number(form.latitude) : undefined,
    longitude: form.longitude ? Number(form.longitude) : undefined,
    address: {
      line1: form.addressLine || undefined,
      city: form.city || undefined,
      state: form.state || undefined,
      pincode: form.pincode || undefined,
      country: 'IN',
    },
    location: form.latitude && form.longitude
      ? { type: 'Point', coordinates: [Number(form.longitude), Number(form.latitude)] }
      : undefined,
    metadata: {
      branchCode: form.branchCode || undefined,
      restaurantTypeCode: form.restaurantTypeCode,
      isPureVeg: form.isPureVeg,
      phoneCountryCode: form.phoneCountryCode || undefined,
      supportPhoneCountryCode: form.supportPhoneCountryCode || undefined,
      sendWelcomeEmail: form.sendWelcomeEmail,
    },
    branchCode: form.branchCode || undefined,
  };
}

export { WEEK_DAYS };

export function normalizeRestaurantSlug(value: string): string {
  return String(value || '')
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48);
}

export function findRestaurantDuplicateErrors(
  rows: Array<{ _id?: string; name?: string; slug?: string }>,
  form: { name: string; slug: string },
  editingId?: string,
): { name?: string; slug?: string } {
  const errors: { name?: string; slug?: string } = {};
  const nameKey = form.name.trim().toLowerCase();
  const slugKey = normalizeRestaurantSlug(form.slug || form.name);
  if (!nameKey && !slugKey) return errors;

  for (const row of rows) {
    if (editingId && String(row._id) === editingId) continue;
    const rowName = String(row.name || '').trim().toLowerCase();
    const rowSlug = normalizeRestaurantSlug(String(row.slug || ''));
    if (nameKey && rowName === nameKey) {
      errors.name = `Restaurant name "${row.name}" already exists — use a different name`;
    }
    if (slugKey && rowSlug === slugKey) {
      errors.slug = `Slug "${slugKey}" is already used by "${row.name}" — choose a unique slug`;
    }
  }
  return errors;
}
